// src/hooks/useGsapImageReveal.ts
import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

type RevealType = "one" | "two" | "three" | "toBottom" | "blogSlug";

export const useGsapImageReveal = (type: RevealType) => {
  useEffect(() => {
    const revealClassMap = {
      one: {
        container: ".rts-reveal-one",
        image: ".rts-reveal-image-one",
        containerX: 0,
        imageX: -100,
      },
      two: {
        container: ".rts-reveal-two",
        image: ".rts-reveal-image-two",
        containerX: 100,
        imageX: -100,
      },
      three: {
        container: ".rts-reveal-three",
        image: ".rts-reveal-image-three",
        containerX: 0,
        imageX: -100,
      },
      toBottom: {
        container: ".rts-reveal-to-bottom-wrapper",
        image: ".rts-reveal-image-toBottom",
        containerX: 100,
        imageX: -100,
      },
      blogSlug: {
        container: ".rts-reveal-one",
        image: ".rts-reveal-image-one",
        containerX: 100,
        imageX: -100,
      },
    };

    const config = revealClassMap[type];
    const revealContainers = document.querySelectorAll<HTMLElement>(config.container);

    const runAnimation = (container: HTMLElement, image: HTMLElement) => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container,
          toggleActions: "restart none none reset",
          start: "top 90%",
          end: "top 0%",
        },
      });

      tl.set(container, { autoAlpha: 1 });

      tl.from(container, {
        xPercent: config.containerX,
        duration: 1.5,
        ease: "power2.out",
      });

      tl.from(image, {
        xPercent: config.imageX,
        scale: 1.3,
        delay: -1.5,
        duration: 1.5,
        ease: "power2.out",
      });

      // Refresh ScrollTrigger after anim setup
      ScrollTrigger.refresh();
    };

    revealContainers.forEach((container) => {
      const image = container.querySelector<HTMLImageElement>(config.image);
      if (!image) return;

      if (image.complete) {
        // Image already in cache
        runAnimation(container, image);
      } else {
        image.addEventListener(
          "load",
          () => {
            runAnimation(container, image);
          },
          { once: true }
        );
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [type]);
};
